import {useAuth} from 'app/modules/auth'
import {Role} from 'app/shared/Common/Enums'
import {HasPermissionInRole} from 'app/shared/Common/Util'
import {FC, lazy, Suspense} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'

const PrivateRoutes = () => {
  const {currentUser} = useAuth()

  // Note: Arrange your page by Name Ascending
  const DashboardPage = lazy(() => import('../pages/dashboard/Wrapper'))
  const MaintenancePage = lazy(() => import('../pages/maintenance/Wrapper'))
  const AnnouncementsPage = lazy(() => import('../pages/announcements/Wrapper'))
  const ProfilePage = lazy(() => import('../pages/profile/Wrapper'))
  const ProgramPage = lazy(() => import('../pages/program/Wrapper'))
  const ReportsPage = lazy(() => import('../pages/reports/Wrapper'))
  const UsersPage = lazy(() => import('../pages/users/Wrapper'))
  const UserManagementPage = lazy(() => import('../pages/administration/user/Wrapper'))
  const UserPermissionPage = lazy(() => import('../pages/administration/permission/Wrapper'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        {HasPermissionInRole(currentUser, [Role.INT_Report_Full, Role.INT_Report_Read]) ? (
          <Route path='auth/*' element={<Navigate to='/dashboard' state={{showPasswordExpiryReminder: true}} />} />
        ) : (
          <Route path='auth/*' element={<Navigate to='/admin/user-management' state={{showPasswordExpiryReminder: true}} />} />
        )}

        {/* Lazy Modules */}
        {HasPermissionInRole(currentUser, [Role.INT_Report_Full, Role.INT_Report_Read]) && (
          <Route
            path='dashboard'
            element={
              <SuspensedView>
                <DashboardPage />
              </SuspensedView>
            }
          />
        )}

        <Route
          path='profile'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        {HasPermissionInRole(currentUser, [Role.INT_User_Full, Role.INT_User_Read, Role.INT_Profile_Full, Role.INT_Profile_Read]) && (
          <Route
            path='users/*'
            element={
              <SuspensedView>
                <UsersPage />
              </SuspensedView>
            }
          />
        )}

        {HasPermissionInRole(currentUser, [Role.INT_Submission_Full, Role.INT_Submission_Read]) && (
          <Route
            path='program/*'
            element={
              <SuspensedView>
                <ProgramPage />
              </SuspensedView>
            }
          />
        )}

        {HasPermissionInRole(currentUser, [Role.INT_Announcement_Full, Role.INT_Announcement_Read]) && (
          <Route
            path='announcements'
            element={
              <SuspensedView>
                <AnnouncementsPage />
              </SuspensedView>
            }
          />
        )}

        {HasPermissionInRole(currentUser, [Role.INT_Report_Full, Role.INT_Report_Read]) && (
          <Route
            path='reports'
            element={
              <SuspensedView>
                <ReportsPage />
              </SuspensedView>
            }
          />
        )}

        {HasPermissionInRole(currentUser, [Role.INT_Maintenance_Full, Role.INT_Maintenance_Read]) && (
          <Route
            path='maintenance/*'
            element={
              <SuspensedView>
                <MaintenancePage />
              </SuspensedView>
            }
          />
        )}

        {HasPermissionInRole(currentUser, [Role.INT_System]) && (
          <Route
            path='admin/user-management'
            element={
              <SuspensedView>
                <UserManagementPage />
              </SuspensedView>
            }
          />
        )}

        {HasPermissionInRole(currentUser, [Role.INT_System]) && (
          <Route
            path='admin/permission'
            element={
              <SuspensedView>
                <UserPermissionPage />
              </SuspensedView>
            }
          />
        )}

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' replace />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
